import { forwardRef, useId, type SelectHTMLAttributes, type ReactNode } from 'react';
import styles from './Select.module.css';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: SelectOption[];
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  fullWidth?: boolean;
  placeholder?: string;
}

/**
 * Accessible select component styled consistently with Input.
 *
 * - Uses htmlFor to associate label with select (a11y requirement).
 * - Error messages linked via aria-describedby.
 * - aria-invalid set when error is present.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    {
      label,
      options,
      error,
      hint,
      leftIcon,
      fullWidth = false,
      placeholder,
      className,
      id: externalId,
      ...props
    },
    ref,
  ) {
    const generatedId = useId();
    const selectId = externalId ?? generatedId;
    const errorId = `${selectId}-error`;
    const hintId = `${selectId}-hint`;

    const describedBy =
      [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') ||
      undefined;

    const wrapperClassNames = [
      styles.wrapper,
      fullWidth ? styles.fullWidth : '',
      className ?? '',
    ]
      .filter(Boolean)
      .join(' ');

    const selectClassNames = [
      styles.select,
      error ? styles.selectError : '',
      leftIcon ? styles.hasLeftIcon : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClassNames}>
        <label htmlFor={selectId} className={styles.label}>
          {label}
        </label>
        <div className={styles.selectContainer}>
          {leftIcon && (
            <span className={styles.leftIcon} aria-hidden="true">
              {leftIcon}
            </span>
          )}
          <select
            ref={ref}
            id={selectId}
            className={selectClassNames}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {hint && !error && (
          <p id={hintId} className={styles.hint}>
            {hint}
          </p>
        )}
        {error && (
          <p id={errorId} className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);
